import { ButtonSecondary, Colors, Text } from '@amelio/ui-library';

export interface AiUnavailableNoticeProps {
    /** Shows the message and the retry. The notice stays mounted while this is false. */
    isUnavailable: boolean;
    /** What could not be produced, said calmly: never the raw error. */
    message: string;
    retryLabel: string;
    onRetry: () => void;
    disabled?: boolean;
    dataTestId?: string;
    retryButtonDataTestId?: string;
}

/**
 * The quiet state of an AI result that did not come: a sentence and a way to ask again, in the
 * section that was waiting for it. An error toast is for an action that just failed under the user's
 * hand; a result missing when a page opens is neither news nor an error.
 *
 * The message sits in a status region mounted before it appears, since a region mounted together with
 * its text is often not announced. The retry stays outside that region, so its label is not read out
 * as part of the message.
 */
export const AiUnavailableNotice = ({
    isUnavailable,
    message,
    retryLabel,
    onRetry,
    disabled = false,
    dataTestId = 'ai-unavailable-notice',
    retryButtonDataTestId = `${dataTestId}-retry`,
}: AiUnavailableNoticeProps) => (
    <div
        className={'flex flex-col items-start gap-3'}
        data-testid={dataTestId}
    >
        <div role={'status'}>
            {isUnavailable && (
                <Text
                    size={'small'}
                    color={Colors.BASE_600}
                >
                    {message}
                </Text>
            )}
        </div>
        {isUnavailable && (
            <ButtonSecondary
                onClick={onRetry}
                disabled={disabled}
                size={'sm'}
                fitContent
                dataTestId={retryButtonDataTestId}
            >
                {retryLabel}
            </ButtonSecondary>
        )}
    </div>
);
